'use client';

import React, { useState, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Paper, Button, IconButton, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { useTrks, useUpdateTrkAsset } from '@/hooks/api/useTrks';
import { useLocations } from '@/hooks/useLocation';
import { orderBy } from 'lodash';
import { Download, Delete, Restore, Edit } from '@mui/icons-material';
import TitleBar from '../layout/TitleBar';
import { useNavigate } from 'react-router-dom';
import { useAuthData } from '@/hooks/getAuthData';
import { useNotification } from '@/contexts/NotificationContext';

const TrkPage: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin');
  const { data: trksData } = useTrks();
  const { data: locationsData } = useLocations();
  const updateTrkAssetMutation = useUpdateTrkAsset();
  const { showSuccess, showError } = useNotification();

  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<any>(null);

  const allTrks = useMemo(() => {
    if (!trksData) return [];
    return Array.isArray(trksData) ? trksData : Object.values(trksData);
  }, [trksData]);

  // Filter out inactive assets by default
  const trks = useMemo(() => {
    if (showInactive) {
      return allTrks;
    }
    return allTrks.filter((trk: any) => trk.active !== false);
  }, [allTrks, showInactive]);

  const locations = useMemo(() => {
    if (!locationsData) return [];
    const locs = Array.isArray(locationsData) ? locationsData : Object.values(locationsData);
    return orderBy(locs, ['name'], ['asc']);
  }, [locationsData]);
  // Generate Location Filter Options (string for filter, number for edit select)
  const locationOptions = locations.map((location: any) => ({
    value: location.id.toString(),
    label: location.name,
  }));
  const locationEditOptions = locations.map((location: any) => ({
    value: location.id,
    label: location.name,
  }));

  const handleDelete = (trk: any) => {
    setAssetToDelete(trk);
    setDeleteConfirmOpen(true);
  };

  const handleReactivate = (trk: any) => {
    setAssetToDelete(trk);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (assetToDelete) {
      const isReactivating = assetToDelete.active === false || assetToDelete.active === null;
      const newActiveValue = isReactivating ? true : false;
      
      console.log('Updating TRK asset:', {
        id: assetToDelete.id,
        currentActive: assetToDelete.active,
        isReactivating,
        newActiveValue
      });
      
      updateTrkAssetMutation.mutate({ id: assetToDelete.id, active: newActiveValue }, {
        onSuccess: () => {
          setDeleteConfirmOpen(false);
          setAssetToDelete(null);
          showSuccess(isReactivating ? 'TRK reactivated successfully' : 'TRK deactivated successfully');
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to update TRK');
        },
      });
    }
  };

  // Column visibility state with localStorage persistence
  const getStoredColumnVisibility = () => {
    const stored = localStorage.getItem('trkPage_columnVisibility');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const saveColumnVisibility = (visibility: Record<string, boolean>) => {
    localStorage.setItem('trkPage_columnVisibility', JSON.stringify(visibility));
  };

  const defaultColumnVisibility = {
    assetId: true,
    locationId: true,
    active: false,
    id: false,
    position: false,
    createdAt: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const stored = getStoredColumnVisibility();
    return stored || defaultColumnVisibility;
  });

  const handleSaveRow = async ({ exitEditingMode, row, values }: any) => {
    const trk = row.original;
    const updatedAssetId = values.assetId;
    const updatedLocationId = values.locationId != null ? Number(values.locationId) : undefined;

    const payload: { id: number; assetId?: string; locationId?: number } = { id: trk.id };
    if (updatedAssetId !== trk.assetId) payload.assetId = updatedAssetId;
    if (updatedLocationId !== undefined && updatedLocationId !== trk.locationId) payload.locationId = updatedLocationId;

    if (payload.assetId !== undefined || payload.locationId !== undefined) {
      updateTrkAssetMutation.mutate(payload, {
        onSuccess: () => {
          exitEditingMode();
          showSuccess('TRK updated successfully');
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to update TRK');
        },
      });
    } else {
      exitEditingMode();
    }
  };

  const trkColumns = [
    { 
      accessorKey: 'assetId', 
      header: 'TRK ID',
      enableEditing: hasAdminRole,
      Cell: ({ row }: any) => {
        return <span style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }} onClick={() => navigate(`/app/trk/${row.original.id}`)}>{row.original.assetId || row.original.id}</span>
      },
    },
    {
      accessorKey: 'locationId',
      header: 'Location',
      enableEditing: hasAdminRole,
      editVariant: 'select',
      editSelectOptions: locationEditOptions,
      Cell: ({ row }: any) => {
        const location = locations.find((loc: any) => loc.id === row.original.locationId);
        return <span style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }} onClick={() => navigate(`/app/locations/${row.original.locationId}`)}>{location?.name || 'No name'}</span>
      },
      filterVariant: 'select',
      filterSelectOptions: locationOptions,
    },
    {
      accessorKey: 'active', header: 'Active',
      Cell: ({ row }: any) => {
        return row.original.active !== false ? 'Yes' : 'No';
      },
      enableHiding: true,
    },
    {
      accessorKey: 'position', header: 'Position',
      Cell: ({ row }: any) => row.original.position || '-',
      enableHiding: true,
    },
    {
      accessorKey: 'id', header: 'ID',
      Cell: ({ row }: any) => row.original.id,
      enableHiding: true,
    },
    {
      accessorKey: 'createdAt', header: 'Created At',
      Cell: ({ row }: any) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
    {
      accessorKey: 'updatedAt', header: 'Updated At',
      Cell: ({ row }: any) => row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'TRKs' }, // No href for the current page
  ];

  const rightContent = (
    <>
      <Button
        variant="outlined"
        startIcon={<Download />}
        sx={{ textTransform: 'none' }}
      >
        Export
      </Button>
      <Button onClick={() => {
      }} variant="contained" sx={{ textTransform: 'none' }}>
        + Add TRK
      </Button>
    </>
  );

  return (
    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title="TRKs"
        rightContent={rightContent}
      />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label="Show Inactive"
        />
      </Box>

      <MaterialReactTable 
        columns={trkColumns as any} 
        data={trks} 
        initialState={{ 
          density: 'compact',
          columnVisibility: columnVisibility
        }}
        state={{
          columnVisibility: columnVisibility
        }}
        onColumnVisibilityChange={(updater) => {
          const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
          setColumnVisibility(newVisibility);
          saveColumnVisibility(newVisibility);
        }}
        enableEditing={hasAdminRole}
        onEditingRowSave={handleSaveRow}
        enableRowActions={hasAdminRole}
        renderRowActions={({ row, table }) => (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <IconButton
              onClick={() => table.setEditingRow(row)}
              size="small"
              title="Edit"
            >
              <Edit />
            </IconButton>
            {row.original.active === false || row.original.active === null ? (
              <IconButton
                size="small"
                onClick={() => handleReactivate(row.original)}
                color="primary"
                title="Reactivate"
              >
                <Restore />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => handleDelete(row.original)}
                color="error"
                title="Delete"
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        )}
      />

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>
          {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Confirm Reactivate' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? (
              <>Are you sure you want to reactivate TRK <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>?</>
            ) : (
              <>Are you sure you want to delete TRK <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>? 
              This will set it as inactive.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            variant="contained" 
            color={(assetToDelete?.active === false || assetToDelete?.active === null) ? "primary" : "error"}
          >
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Reactivate' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrkPage;
